import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { usePostHog } from 'posthog-js/react'
import { useCFAuth } from '@/lib/auth/cf-auth-context'
import { buildGettingStartedPath } from '@/lib/cloudflare/billing-client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

const signinSearchSchema = z.object({
  callbackUrl: z.string().optional(),
  error: z.string().optional(),
})

export const Route = createFileRoute('/auth/signin')({
  validateSearch: signinSearchSchema,
  component: SignInRoute,
})

function SignInRoute() {
  const posthog = usePostHog()
  const { signIn, status } = useCFAuth()
  const { callbackUrl, error } = Route.useSearch()

  // Redirect if already authenticated
  if (status === 'authenticated') {
    window.location.href = callbackUrl || '/'
    return null
  }

  const handleSignIn = () => {
    posthog.capture('clicked_sign_in', { method: 'google' })
    signIn(callbackUrl || buildGettingStartedPath())
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Welcome to Winbox Zero</CardTitle>
          <CardDescription>
            Sign in with your Google account to connect your inbox and continue setup
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">
              {error === 'OAuthAccountNotLinked'
                ? 'This email is already linked to another account.'
                : 'An error occurred during sign in. Please try again.'}
            </div>
          )}
          <Button
            onClick={handleSignIn}
            className="w-full"
            size="lg"
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Connect Gmail to continue
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            By signing in, you agree to our{' '}
            <a href="/terms" className="underline hover:text-primary">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="underline hover:text-primary">
              Privacy Policy
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
